import { __extends } from "tslib";
import { MDCTabScroller } from '@material/tab-scroller';
import { getActiveElement } from 'shadow-utils';
import { getShadowElement, removeClass, isFunction } from '@tyler-components-web/core';
import { toggleClass } from '../../core/utils';
import { BaseAdapter } from '../../core/adapters/base-adapter';
import { TAB_BAR_CONSTANTS } from './tab-bar-constants';
import { TAB_CONSTANTS } from '../tab/tab-constants';
var TabBarAdapter = /** @class */ (function (_super) {
    __extends(TabBarAdapter, _super);
    function TabBarAdapter(component) {
        var _this = _super.call(this, component) || this;
        _this._tabs = [];
        _this._rootElement = getShadowElement(_this._component, TAB_BAR_CONSTANTS.selectors.ROOT);
        _this._tabScrollElement = getShadowElement(_this._component, TAB_BAR_CONSTANTS.selectors.TAB_SCROLLER);
        _this._tabScrollAreaElement = getShadowElement(_this._component, TAB_BAR_CONSTANTS.selectors.TAB_SCROLLER_AREA);
        _this._tabScrollContentElement = getShadowElement(_this._component, TAB_BAR_CONSTANTS.selectors.TAB_SCROLLER_CONTENT);
        _this._defaultSlotElement = getShadowElement(_this._component, TAB_CONSTANTS.selectors.DEFAULT_SLOT);
        _this._prevButtonContainerElement = getShadowElement(_this._component, TAB_BAR_CONSTANTS.selectors.PREV_BUTTON_CONTAINER);
        _this._prevButtonElement = getShadowElement(_this._component, TAB_BAR_CONSTANTS.selectors.PREV_BUTTON);
        _this._nextButtonContainerElement = getShadowElement(_this._component, TAB_BAR_CONSTANTS.selectors.NEXT_BUTTON_CONTAINER);
        _this._nextButtonElement = getShadowElement(_this._component, TAB_BAR_CONSTANTS.selectors.NEXT_BUTTON);
        return _this;
    }
    TabBarAdapter.prototype.initializeTabs = function (activeTabIndex, stretch) {
        this._tabs = this._getTabs();
        if (!this._tabs.length) {
            return;
        }
        this._tabs.forEach(function (tab) {
            tab.active = false;
            tab.stretch = stretch;
        });
        // Set the initial active tab
        if (activeTabIndex >= 0 && activeTabIndex < this._tabs.length) {
            this._tabs[activeTabIndex].active = true;
        }
        else {
            this.ensureFocusableTab();
        }
    };
    TabBarAdapter.prototype.initializeTabScroller = function () {
        this._tabScroller = new MDCTabScroller(this._tabScrollElement);
    };
    TabBarAdapter.prototype.destroyTabScroller = function () {
        if (this._tabScroller) {
            this._tabScroller.destroy();
        }
    };
    TabBarAdapter.prototype.addTabChangeListener = function (listener) {
        this._defaultSlotElement.addEventListener('slotchange', listener);
    };
    TabBarAdapter.prototype.removeTabChangeListener = function (listener) {
        this._defaultSlotElement.removeEventListener('slotchange', listener);
    };
    TabBarAdapter.prototype.addScrollListener = function (listener) {
        this._tabScrollAreaElement.addEventListener('scroll', listener);
    };
    TabBarAdapter.prototype.getTabIndex = function (tab) {
        return this._tabs.indexOf(tab);
    };
    TabBarAdapter.prototype.getTabCount = function () {
        return this._tabs.length;
    };
    TabBarAdapter.prototype.activateTab = function (index, previousTabBounds) {
        var tab = this._getTabByIndex(index);
        if (tab) {
            tab.activate(previousTabBounds);
        }
    };
    TabBarAdapter.prototype.deactivateTab = function (index) {
        var tab = this._getTabByIndex(index);
        if (tab) {
            tab.deactivate();
        }
    };
    TabBarAdapter.prototype.syncTabIndex = function (index) {
        var tabs = this._getTabs();
        var activeTab = tabs[index];
        tabs.filter(function (t) { return t !== activeTab; })
            .forEach(function (t) { return t.setTabIndex(-1); });
    };
    TabBarAdapter.prototype.getTabBounds = function (index) {
        var tab = this._getTabByIndex(index);
        if (tab) {
            return tab.computeIndicatorBounds();
        }
        return undefined;
    };
    TabBarAdapter.prototype.setUnderline = function (value) {
        toggleClass(this._rootElement, value, TAB_BAR_CONSTANTS.classes.UNDERLINED);
    };
    TabBarAdapter.prototype.setStacked = function (value) {
        toggleClass(this._rootElement, value, TAB_BAR_CONSTANTS.classes.STACKED);
    };
    TabBarAdapter.prototype.scrollTo = function (scrollX) {
        if (this._tabScroller) {
            this._tabScroller.scrollTo(scrollX);
        }
    };
    TabBarAdapter.prototype.getScrollContentWidth = function () {
        return this._tabScroller ? this._tabScroller.getScrollContentWidth() : 0;
    };
    TabBarAdapter.prototype.getScrollPosition = function () {
        return this._tabScroller ? this._tabScroller.getScrollPosition() : 0;
    };
    TabBarAdapter.prototype.getOffsetWidth = function () {
        return this._tabScrollElement.offsetWidth;
    };
    TabBarAdapter.prototype.getTabDimensionsAtIndex = function (index) {
        var tab = this._getTabByIndex(index);
        if (tab) {
            return tab.computeDimensions();
        }
        return {
            rootLeft: 0,
            rootRight: 0,
            contentLeft: 0,
            contentRight: 0
        };
    };
    TabBarAdapter.prototype.incrementScroll = function (scrollXIncrement) {
        if (this._tabScroller) {
            this._tabScroller.incrementScroll(scrollXIncrement);
        }
    };
    TabBarAdapter.prototype.isScrolled = function () {
        var position = this._tabScrollAreaElement.scrollLeft;
        return position > 0;
    };
    TabBarAdapter.prototype.isScrolledEnd = function () {
        var position = this._tabScrollAreaElement.scrollLeft;
        var scrollWidth = this._tabScrollContentElement.scrollWidth - this._tabScrollElement.offsetWidth;
        return position >= scrollWidth;
    };
    TabBarAdapter.prototype.getFocusedTabIndex = function () {
        var activeElement = getActiveElement();
        return this._tabs.findIndex(function (tab) {
            if (tab === activeElement) {
                return true;
            }
            return !!tab.shadowRoot.contains(activeElement);
        });
    };
    TabBarAdapter.prototype.canFocusTab = function (index) {
        var tab = this._getTabByIndex(index);
        return tab.focusOnActivate;
    };
    TabBarAdapter.prototype.focusTabAtIndex = function (index) {
        var tab = this._getTabByIndex(index);
        if (tab) {
            return tab.focus();
        }
    };
    TabBarAdapter.prototype.setLayoutMode = function (value) {
        var isFixed = value === 'fixed';
        toggleClass(this._rootElement, isFixed, TAB_BAR_CONSTANTS.classes.FIXED);
        this._tabs.forEach(function (tab) { return tab.stretch = isFixed; });
    };
    TabBarAdapter.prototype.setLayoutAlign = function (value) {
        removeClass([
            TAB_BAR_CONSTANTS.classes.ALIGN_CENTER,
            TAB_BAR_CONSTANTS.classes.ALIGN_END
        ], this._rootElement);
        switch (value) {
            case 'center':
                this._rootElement.classList.add(TAB_BAR_CONSTANTS.classes.ALIGN_CENTER);
                break;
            case 'end':
                this._rootElement.classList.add(TAB_BAR_CONSTANTS.classes.ALIGN_END);
                break;
        }
    };
    TabBarAdapter.prototype.setFocusOnActivate = function (value) {
        this._tabs.forEach(function (tab) { return tab.focusOnActivate = value; });
    };
    TabBarAdapter.prototype.setPreviousButtonVisibility = function (value) {
        if (value) {
            this._prevButtonContainerElement.style.removeProperty('display');
        }
        else {
            this._prevButtonContainerElement.style.display = 'none';
        }
    };
    TabBarAdapter.prototype.setPreviousButtonEnabled = function (value) {
        this._prevButtonElement.disabled = !value;
    };
    TabBarAdapter.prototype.setNextButtonVisibility = function (value) {
        if (value) {
            this._nextButtonContainerElement.style.removeProperty('display');
        }
        else {
            this._nextButtonContainerElement.style.display = 'none';
        }
    };
    TabBarAdapter.prototype.setNextButtonEnabled = function (value) {
        this._nextButtonElement.disabled = !value;
    };
    TabBarAdapter.prototype.addScrollPreviousListener = function (type, listener) {
        this._prevButtonElement.addEventListener(type, listener);
    };
    TabBarAdapter.prototype.addScrollNextListener = function (type, listener) {
        this._nextButtonElement.addEventListener(type, listener);
    };
    TabBarAdapter.prototype.isTabDisabled = function (index) {
        var tab = this._getTabByIndex(index);
        return tab ? tab.disabled : true;
    };
    TabBarAdapter.prototype.ensureFocusableTab = function () {
        var tabs = this._getTabs();
        var hasActiveTab = tabs.some(function (tab) { return tab.active; });
        if (!hasActiveTab && tabs.length) {
            var firstTab = tabs[0];
            if (isFunction(firstTab.setTabIndex)) {
                tabs[0].setTabIndex(0);
            }
        }
    };
    TabBarAdapter.prototype._getTabs = function () {
        return Array.from(this._component.querySelectorAll(TAB_CONSTANTS.elementName));
    };
    TabBarAdapter.prototype._getTabByIndex = function (index) {
        return this._tabs[index];
    };
    return TabBarAdapter;
}(BaseAdapter));
export { TabBarAdapter };
